import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import EnhancedTeamManagementTable from './components/EnhancedTeamManagement';
import { MdDelete, MdEdit, MdError, MdMoreVert, MdAdd, MdSportsRugby, MdSportsSoccer, MdSportsBasketball, MdSports, MdShield, MdSportsHockey } from 'react-icons/md';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AddTeamModal from "./components/AddTeamModal.jsx"
import EditTeamModal from './components/EditTeamModal';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import Loading from '../../../components/loading';
import { Circle } from 'lucide-react';

const KebabMenu = ({ teamId, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleMenu}
        className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        <MdMoreVert className="h-6 w-6" />
      </button>
      {isOpen && (
        <div className="absolute right-full top-0 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg z-50 mr-2">
          <div className="py-1">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEdit(teamId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdEdit className="mr-2" /> Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(teamId);
                setIsOpen(false);
              }}
              className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <MdDelete className="mr-2" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const TeamManagement = () => {
  const [teamData, setTeamData] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [sports, setSports] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();

  const fetchData = async (url, setter) => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.log('No token found, redirecting to sign-in');
      navigate('/auth/sign-in');
      return;
    }

    const headers = new Headers();
    headers.set('Authorization', `Token ${token}`);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 500) {
          throw new Error('Server error. The team is working on resolving this issue.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setter(data);
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      setError(error.message);
      toast.error(`Failed to fetch data: ${error.message}`);
    }
  };

  useEffect(() => {
    const fetchTeamsData = async () => {
      setIsLoading(true);
      await fetchData('https://staging-sit.com/api/teams/', setTeamData);
      setIsLoading(false);
    };

    fetchTeamsData();
  }, [retryCount, navigate]);

  useEffect(() => {
    const fetchBackgroundData = async () => {
      if (!isLoading) {
        setIsBackgroundLoading(true);
        await Promise.all([
          fetchData('https://staging-sit.com/api/coaches/', setCoaches),
          fetchData('https://staging-sit.com/api/team-categories/', setTeamCategories),
          fetchData('https://staging-sit.com/api/sports/', setSports),
        ]);
        setIsBackgroundLoading(false);
      }
    };

    fetchBackgroundData();
  }, [isLoading]);

  const formatSport = (sport) => {
    return sport.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatTeamCategory = (category) => {
    if (category.toLowerCase().startsWith('under')) {
      const [under, age] = category.split(/(\d+)/);
      return `Under ${age}`;
    }
    return category;
  };

  const openDeleteModal = useCallback((id) => {
    setTeamToDelete(id);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setTeamToDelete(null);
    setIsDeleting(false);
  };

  const handleDelete = async () => {
    if (!teamToDelete) return;

    setIsDeleting(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/auth/sign-in');
        return;
      }

      const headers = new Headers();
      headers.set('Authorization', `Token ${token}`);
      headers.set('Content-Type', 'application/json');

      const response = await fetch(`https://staging-sit.com/api/teams/${teamToDelete}/`, {
        method: 'DELETE',
        headers: headers,
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setTeamData(prevData => prevData.filter(team => team.id !== teamToDelete));
      toast.success("Team deleted successfully");
      closeDeleteModal();
    } catch (error) {
      console.error('Error deleting team:', error);
      toast.error(`Failed to delete team: ${error.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddTeam = useCallback(async (newTeamData) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/auth/sign-in');
        return false;
      }

      const headers = new Headers();
      headers.set('Authorization', `Token ${token}`);
      headers.set('Content-Type', 'application/json');

      const response = await fetch('https://staging-sit.com/api/teams/', {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(newTeamData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const addedTeam = await response.json();
      setTeamData(prevData => [...prevData, addedTeam]);
      toast.success("Team added successfully");
      setIsAddModalOpen(false);
      return true;
    } catch (error) {
      console.error('Error adding team:', error);
      toast.error(`Failed to add team: ${error.message}`);
      return false;
    }
  }, [navigate]);

  const handleRowClick = (rowData) => {
    console.log("Clicked row data:", rowData);
    // Implement logic to open team details
  };

  const handleEdit = useCallback((id) => {
    const teamToEdit = teamData.find(team => team.id === id);
    if (teamToEdit) {
      setTeamToEdit(teamToEdit);
      setIsEditModalOpen(true);
    } else {
      console.error(`Team with id ${id} not found`);
      toast.error("Error: Team not found");
    }
  }, [teamData]);

  const handleUpdateTeam = useCallback(async (updatedTeamData) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/auth/sign-in');
        return false;
      }

      const headers = new Headers();
      headers.set('Authorization', `Token ${token}`);
      headers.set('Content-Type', 'application/json');

      const response = await fetch(`https://staging-sit.com/api/teams/${updatedTeamData.id}/`, {
        method: 'PUT',
        headers: headers,
        credentials: 'include',
        body: JSON.stringify(updatedTeamData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          navigate('/auth/sign-in');
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedTeam = await response.json();
      setTeamData(prevData => prevData.map(team => team.id === updatedTeam.id ? updatedTeam : team));
      setIsEditModalOpen(false);
      return true;
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error(`Failed to update team: ${error.message}`);
      return false;
    }
  }, [navigate]);

  const getSportIcon = (sport) => {
    switch (sport.toLowerCase()) {
      case 'gaelic football':
        return <MdSports className="h-5 w-5 mr-2" />;
      case 'hurling':
      case 'camogie':
        return <Circle className="h-5 w-5 mr-2" />;
      case 'rugby':
        return <MdSportsRugby className="h-5 w-5 mr-2" />;
      case 'soccer':
        return <MdSportsSoccer className="h-5 w-5 mr-2" />;
      case 'hockey':
        return <MdSportsHockey className="h-5 w-5 mr-2" />;
      case 'basketball':
        return <MdSportsBasketball className="h-5 w-5 mr-2" />;
      default:
        return <MdSports className="h-5 w-5 mr-2" />;
    }
  };
  
  const columnsDataColumnsTM = [
    {
      Header: "TEAM NAME",
      accessor: "team_name",
    },
    {
      Header: "NUMBER OF PLAYERS",
      accessor: "number_of_players",
    },
    {
      Header: "SPORT",
      accessor: "sport",
      Cell: ({ value }) => (
        <span className='flex items-center'>
          {getSportIcon(value)}
          {formatSport(value)}
        </span>
      ),
    },
    {
      Header: "TEAM CATEGORY",
      accessor: "team_category",
      Cell: ({ value }) => <span>{formatTeamCategory(value)}</span>,
    },
    {
      Header: "COACHES",
      accessor: "coaches_details",
      Cell: ({ value }) => (
        <div>
          {value && value.length > 0 
            ? value.map((coach) => `${coach.fname} ${coach.lname}`).join(', ') 
            : <div className="flex items-center">
                <MdError style={{ color: 'orange' }} className="h-6 w-6 mr-1" />
                Unassigned
              </div>}
        </div>
      ),
    },
    {
      Header: "PLAYERS",
      accessor: "players_details",
      Cell: ({ value }) => (
        <div>
          {value && value.map((player, index) => `${player.fname} ${player.lname}`).join(', ')}
        </div>
      ),
    },
    {
      Header: "ACTIONS",
      accessor: "id",
      id: "actions",
      disableSortBy: true,
      Cell: ({ value }) => <KebabMenu teamId={value} onEdit={handleEdit} onDelete={openDeleteModal} />,
    },
  ];

  if (isLoading) return <Loading />;
  
  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden p-6">
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-4">Error Occurred</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
        <div className="space-y-4">
          <button
            onClick={() => setRetryCount(prev => prev + 1)}
            className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
          >
            Retry
          </button>
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('user');
              navigate('/auth/sign-in');
            }}
            className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            Logout
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
      <div className="p-4 flex justify-between items-center border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">Team Management</h2>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] dark:bg-[#7551FF] dark:hover:bg-[#9E8CFC] flex items-center"
          disabled={isBackgroundLoading}
        >
          <MdAdd className="mr-2" /> Add New Team
        </button>
      </div>
      <div className="p-4">
        <div className="overflow-x-auto">
          <EnhancedTeamManagementTable
            columnsData={columnsDataColumnsTM}
            tableData={teamData}
            onRowClick={handleRowClick}
          />
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
      {!isBackgroundLoading && (
        <>
          <AddTeamModal
            isOpen={isAddModalOpen}
            onClose={() => setIsAddModalOpen(false)}
            onAdd={handleAddTeam}
            coaches={coaches}
            sports={sports}
            teamCategories={teamCategories}
          />
          <EditTeamModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            onSave={handleUpdateTeam}
            teamData={teamToEdit}
            coaches={coaches}
            sports={sports}
            teamCategories={teamCategories}
          />
        </>
      )}
      <ToastContainer position="bottom-right" />
      {isBackgroundLoading && (
        <div className="fixed bottom-4 right-4 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 px-4 py-2 rounded shadow">
          Loading additional data...
        </div>
      )}
    </div>
  );
};

export default TeamManagement;